/**
 * The project: one set of shared records (guests, agenda, vendors, quotes, expenses, rooms, tasks) plus a few
 * settings, saved to and loaded from a single JSON file the person keeps. This is how connected tools share
 * data and how a project moves between devices, colleagues and, later, EventForge. Nothing is sent anywhere.
 *
 * Loading never trusts the file: every record goes through the same cleaning rules as a CSV import, unknown
 * fields are dropped, and sizes are capped.
 */
import { buildRecord, LAYOUTS, type ImportIssue } from "./layouts";
import { isValidPassKey, newPassKey } from "./passCode";
import { cleanCurrency, cleanText, cleanTimeZone } from "./fields";
import { emptyData, PAPER_SIZES, RECORD_KINDS, type PaperSize, type ProjectData, type RecordKind } from "./types";

export interface Project {
  name: string;
  /** ISO 4217 code used when a record has no currency of its own. */
  currency: string;
  /** BCP 47 language tag, for dates and number formats. */
  locale: string;
  /** IANA time zone that session times are in. */
  timeZone: string;
  paper: PaperSize;
  /** Secret that signs pass codes. Keep the file private. */
  passKey: string;
  data: ProjectData;
}

export interface ProjectFile { app: "freedailypro"; kind: "project"; version: 1; saved: string; project: Project }

export const PROJECT_FILE_VERSION = 1;
export const MAX_PROJECT_BYTES = 25_000_000;
export const MAX_RECORDS_PER_KIND = 20000;

function browserTimeZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}

export function emptyProject(opts: Partial<Omit<Project, "data">> = {}): Project {
  return {
    name: opts.name ?? "My event",
    currency: opts.currency ?? "USD",
    locale: opts.locale ?? "en-US",
    timeZone: opts.timeZone ?? browserTimeZone(),
    paper: opts.paper ?? "letter",
    passKey: opts.passKey ?? newPassKey(),
    data: emptyData(),
  };
}

export function serializeProject(project: Project, now = new Date()): string {
  const file: ProjectFile = { app: "freedailypro", kind: "project", version: 1, saved: now.toISOString(), project };
  return JSON.stringify(file, null, 2);
}

export type ParseProjectResult =
  | { ok: true; project: Project; issues: ImportIssue[] }
  | { ok: false; error: string };

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** Turns any scalar a JSON file might hold into the text the field cleaners expect. */
function asText(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  if (typeof v === "string") return v;
  return "";
}

export function parseProjectFile(text: string): ParseProjectResult {
  if (text.length > MAX_PROJECT_BYTES) return { ok: false, error: "That file is too large to be a project file." };
  let j: unknown;
  try { j = JSON.parse(text); } catch { return { ok: false, error: "That isn't a project file (it isn't valid JSON)." }; }
  if (!isObj(j) || j.app !== "freedailypro" || j.kind !== "project" || !isObj(j.project)) return { ok: false, error: "That isn't a FreeDailyPro project file." };
  if (typeof j.version !== "number" || j.version > PROJECT_FILE_VERSION) return { ok: false, error: "That project file was made by a newer version. Reload the page and try again." };

  const p = j.project;
  const issues: ImportIssue[] = [];
  const warn = (message: string) => issues.push({ row: 0, level: "warning", message });

  const name = cleanText(asText(p.name), 120);
  const cur = cleanCurrency(asText(p.currency));
  const tz = cleanTimeZone(asText(p.timeZone));
  const locale = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(asText(p.locale)) ? asText(p.locale) : "en-US";
  const paper: PaperSize = (PAPER_SIZES as readonly string[]).includes(asText(p.paper)) ? (asText(p.paper) as PaperSize) : "letter";
  let passKey = asText(p.passKey);
  if (!isValidPassKey(passKey)) { passKey = newPassKey(); warn("The pass key was missing or damaged, so a new one was made. Passes printed before will not check in."); }

  const project: Project = {
    name: name.ok && name.value ? name.value : "My event",
    currency: cur.ok ? cur.value : "USD",
    locale,
    timeZone: tz.ok ? tz.value : browserTimeZone(),
    paper,
    passKey,
    data: emptyData(),
  };

  const data = isObj(p.data) ? p.data : {};
  for (const kind of RECORD_KINDS) {
    const list = data[kind];
    if (list === undefined) continue;
    if (!Array.isArray(list)) { warn(`The ${LAYOUTS[kind].label.toLowerCase()} list was not a list, so it was ignored.`); continue; }
    const seen = new Set<string>();
    const out: unknown[] = [];
    list.slice(0, MAX_RECORDS_PER_KIND).forEach((item, i) => {
      if (!isObj(item)) { issues.push({ row: i + 1, level: "error", message: `${LAYOUTS[kind].label}: entry is not a record` }); return; }
      const before = issues.length;
      const rec = buildRecord(kind, (col) => asText(item[col.key]), { currency: project.currency }, i + 1, issues, seen);
      for (let k = before; k < issues.length; k++) issues[k] = { ...issues[k], message: `${LAYOUTS[kind].label}: ${issues[k].column ? issues[k].column + " " : ""}${issues[k].message}` };
      if (rec) out.push(rec);
    });
    if (list.length > MAX_RECORDS_PER_KIND) warn(`Only the first ${MAX_RECORDS_PER_KIND} ${LAYOUTS[kind].label.toLowerCase()} were read.`);
    (project.data as unknown as Record<RecordKind, unknown[]>)[kind] = out;
  }
  return { ok: true, project, issues };
}
